import { rands } from './graph-point';

/**
 * Where the 7R graph's target line stands right now (OE-265). The graph reports it; the page turns it
 * into "ahead of pace" and a projected close.
 */
export interface TargetPace {
    /** The target the line ends at (0 = no target line). */
    target: number;
    /** What the target stands for, e.g. "Company target" (target-label.ts). */
    label: string;
    /** How far through the period "now" is along the target line: 0 = not started, 1 = over. */
    fraction: number;
    /** The Total line's running total now (the graph reports it with the pace). */
    actual?: number;
    /** The company's own target for the period, when the viewer may see it (0 otherwise). */
    companyTarget?: number;
}

export type PaceState = 'not-started' | 'ahead' | 'on-pace' | 'behind' | 'finished';

export interface PaceReading {
    state: PaceState;
    /** Where the target line is now. */
    expected: number;
    /** actual - expected (negative = behind). */
    gap: number;
    /** actual as a share of the expected-by-now figure, e.g. 1.12 = 12% ahead. */
    ratio: number;
    /** actual / fraction: where the period closes if it carries on at this rate (omitted early on). */
    projected?: number;
    /** actual as a share of the whole target. */
    ofTarget: number;
}

/** Within 2% of the line counts as on pace. */
const ON_PACE_BAND = 0.02;
/** Too little of the period gone to project from (the first ~5%). */
const MIN_FRACTION_TO_PROJECT = 0.05;

export function readPace(pace: TargetPace | null | undefined, actual: number): PaceReading | null {
    if (!pace || !(pace.target > 0)) return null;
    const fraction = Math.min(1, Math.max(0, pace.fraction || 0));
    const ofTarget = actual / pace.target;
    if (fraction <= 0) return { state: 'not-started', expected: 0, gap: actual, ratio: 0, ofTarget };
    const expected = pace.target * fraction;
    const gap = actual - expected;
    const ratio = expected > 0 ? actual / expected : 0;
    if (fraction >= 1) return { state: 'finished', expected, gap, ratio, ofTarget, projected: actual };
    const state: PaceState = ratio >= 1 + ON_PACE_BAND ? 'ahead' : ratio <= 1 - ON_PACE_BAND ? 'behind' : 'on-pace';
    const projected = fraction >= MIN_FRACTION_TO_PROJECT ? actual / fraction : undefined;
    return { state, expected, gap, ratio, ofTarget, projected };
}

/**
 * When the line shows a rep selection's summed target and it differs from the company target, the
 * sentence that explains why the two views of "everyone" disagree. Null when there is nothing to say.
 */
export function targetGapNote(pace: TargetPace | null | undefined, allRepsSelected: boolean): string | null {
    if (!pace || !allRepsSelected) return null;
    const company = pace.companyTarget || 0;
    if (!(company > 0) || !(pace.target > 0)) return null;
    const diff = pace.target - company;
    if (Math.abs(diff) < 1) return null;
    const r = rands;
    return `The reps' own targets add up to ${r(pace.target)}, ${r(Math.abs(diff))} ${diff > 0 ? 'more' : 'less'} than the company target of ${r(company)}.`;
}
